import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router";
import {
  CollapseDownIcon,
  CollapseUpIcon,
  DeleteIcon,
  DragAndOrderIcon,
} from "../../../../assets/icons/index";
import Constant from "../../../../utils/constant";
import { ILiftOffGalaxyTopic } from "../../../../model/interface/liftOffGalaxyStandard";

interface IGalaxyTabSortableItemProps {
  topic: ILiftOffGalaxyTopic;
  index: number;
  toggle: number | undefined;
  onStandardSelection: (
    e: React.ChangeEvent<HTMLInputElement>,
    standardId: number,
    isStandardGrouped: boolean,
    index: number
  ) => void;
  handleShowGroupedStandards: (
    standardId: number,
    index?: number,
    type?: string
  ) => void;
  confirmDeleteStandard: (standardId: number, orderNo: number) => void;
}

const GalaxyTabSortableItem: React.FC<IGalaxyTabSortableItemProps> = ({
  topic,
  index,
  toggle,
  onStandardSelection,
  handleShowGroupedStandards,
  confirmDeleteStandard,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: topic.standardId.toString() + "-" + index,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Separate drag handle styles
  const dragHandleStyle = {
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
    >
      <td className="px-3 py-2">
        <div
          {...attributes}
          {...listeners}
          style={dragHandleStyle}
          className="inline-flex items-center justify-center"
        >
          <DragAndOrderIcon className="w-6 h-6" />
        </div>
      </td>
      <td>
        <label className="flex items-center mr-2">
          <input
            id={topic.standardId.toString()}
            name="isActive"
            type="checkbox"
            onChange={(e) =>
              onStandardSelection(
                e,
                topic.standardId,
                topic.isStandardGrouped,
                index
              )
            }
            className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
          />
        </label>
      </td>
      <td>
        {topic.isStandardGrouped && (
          <button
            onClick={() => {
              const action = index === toggle ? "hide" : "show";
              handleShowGroupedStandards(topic?.standardId, index, action);
            }}
            className="p-1 hover:bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-color"
          >
            {index === toggle ? (
              <CollapseUpIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <CollapseDownIcon className="w-5 h-5 text-gray-500" />
            )}
          </button>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
        {topic.orderNumber}
      </td>
      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
        {topic.code ? topic.code : "-"}
      </td>
      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
        {topic.isStandardGrouped ? (
          <Link
            className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
            to={Constant.STANDARDGROUPEDIT.replace(
              ":id",
              topic.standardId.toString()
            )}
          >
            {topic.standardName}
          </Link>
        ) : (
          <Link
            to={Constant.EDITSTANDARD.replace(
              ":id",
              topic.standardId ? topic.standardId.toString() : ""
            )}
            className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
          >
            {topic.standardName}
          </Link>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
        {topic.isStandardGrouped ? "yes" : "No"}
      </td>
      <td className="px-3 py-2 text-left cursor-pointer font-medium text-indigo-600 hover:text-red-500 transaction ease-in-out duration-300">
        <DeleteIcon
          className="w-4 text-xs cursor-pointer hover:text-red-500 transaction ease-in-out duration-300 text-gray-700"
          onClick={() => {
            confirmDeleteStandard(topic.standardId, topic.orderNumber);
          }}
        />
      </td>
    </tr>
  );
};

export default GalaxyTabSortableItem;
